<template>
    <DCard padding>
        <DRow>
            <DRow
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol
                    sm="3"
                    class="pt-1"
                >
                    <DInput
                        id="search"
                        v-model="filterParams.search"
                        :label="__('messages.search')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DCol>
                <DTable
                    class="table-responsive"
                    :sorter="isSortable"
                    :sorter-value="sorterValue"
                    :items="domainConfigurations.data"
                    :fields="fields"
                    hover
                    @update:sorter-value="updateOrdering"
                >
                    <template #domain_update_allowed="{item}">
                        <td>
                            <DTag
                                v-if="item.domain_update_allowed === true"
                                :label="__('messages.yes')"
                                color="success"
                            />
                            <DTag
                                v-else
                                :label="__('messages.no')"
                                color="danger"
                            />
                        </td>
                    </template>
                    <template #domain_trade_allowed="{item}">
                        <td>
                            <DTag
                                v-if="item.domain_trade_allowed === true"
                                :label="__('messages.yes')"
                                color="success"
                            />
                            <DTag
                                v-else
                                :label="__('messages.no')"
                                color="danger"
                            />
                        </td>
                    </template>
                    <template #lock_after_trade="{item}">
                        <td>
                            <DTag
                                v-if="item.lock_after_trade === true"
                                :label="__('messages.yes')"
                                color="success"
                            />
                            <DTag
                                v-else
                                :label="__('messages.no')"
                                color="danger"
                            />
                        </td>
                    </template>
                    <template #dnssec_enabled="{item}">
                        <td>
                            <DTag
                                v-if="item.dnssec_enabled === true"
                                :label="__('messages.yes')"
                                color="success"
                            />
                            <DTag
                                v-else
                                :label="__('messages.no')"
                                color="danger"
                            />
                        </td>
                    </template>
                    <template #idn_character_sets="{item}">
                        <td>
                            <DTag
                                v-for="idnCharacterSet in item.idn_character_sets"
                                :key="idnCharacterSet.id"
                                :label="idnCharacterSet.name"
                                color="info"
                                class="me-1"
                            />
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td class="text-right">
                            <DButton
                                icon="pencil"
                                mini
                                @click="editDomainConfiguration(item)"
                            />
                            <DButton
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="showDeleteConfirmationModal(item)"
                            />
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="domainConfigurations.last_page > 1"
                    :active-page="domainConfigurations.current_page"
                    :pages="domainConfigurations.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DModal
            v-if="deleteConfirmationModalVisible"
            v-model:show="deleteConfirmationModalVisible"
            :title="__('settings.domain_configuration.deletion_confirmation_modal.title')"
            :message="__('settings.domain_configuration.deletion_confirmation_modal.message')"
            is-confirm
            is-error
            @close="hideDeleteConfirmationModal"
            @confirm="deleteDomainConfiguration"
        />
        <DFabs>
            <DFab
                icon="plus"
                :label="__('settings.domain_configuration.create')"
                @click="createDomainConfiguration"
            />
        </DFabs>
    </DCard>
</template>

<script setup>
import { __ } from '~@/Composables/translation'
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'

const props = defineProps({
    domainConfigurations: {
        type: Object,
        default: () => { return {} }
    },
    filters: {
        type: Object,
        default: () => { return {} }
    }
})

const filterParams = {
    search: props.filters.search,
    sort_column: props.filters.sort_column,
    sort_direction: getSortDirection()
}

// Pagination
const fields = [
    { key: 'id', label: __('settings.domain_configuration.id'), sorter: false },
    { key: 'tld', label: __('settings.domain_configuration.tld'), sorter: true },
    { key: 'domain_update_allowed', label: __('settings.domain_configuration.domain_update_allowed'), sorter: false },
    { key: 'domain_update_price', label: __('settings.domain_configuration.domain_update_price'), sorter: false },
    { key: 'domain_trade_allowed', label: __('settings.domain_configuration.domain_trade_allowed'), sorter: false },
    { key: 'domain_trade_price', label: __('settings.domain_configuration.domain_trade_price'), sorter: false },
    { key: 'lock_after_trade', label: __('settings.domain_configuration.lock_after_trade'), sorter: false },
    { key: 'dnssec_enabled', label: __('settings.domain_configuration.dnssec_enabled'), sorter: false },
    { key: 'idn_character_sets', label: __('settings.domain_configuration.idn_character_sets'), sorter: false },
    { key: 'actions', sorter: false, label: '' }
]
const resetFilters = () => {
    router.get(route(
        'settings.domain-configuration.index',
        {
            page: props.filters.page,
            saveFilters: true,
            defaultFilters: true
        }
    ))
}
const changePage = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(filterParams).filter(([_, v]) => v))

    if (page > 1) {
        queryParameters.page = page
    }

    if (filterParams.sort_direction === 1) {
        queryParameters.sort_direction = 'asc'
    }

    if (filterParams.sort_direction === 0) {
        queryParameters.sort_direction = 'desc'
    }

    router.get(route('settings.domain-configuration.index', {
        saveFilters: true
    }), queryParameters)
}

// Sorting
const sorterValue = computed(() => {
    return {
        column: filterParams.sort_column,
        asc: filterParams.sort_direction
    }
})
const isSortable = computed(() => {
    return { external: true }
})
const updateOrdering = (event) => {
    filterParams.sort_column = event.column
    filterParams.sort_direction = event.asc ? 'asc' : 'desc'

    changePage(props.domainConfigurations.current_page)
}

// Create
const createDomainConfiguration = () => {
    router.get(route(
        'settings.domain-configuration.create'
    ))
}

// Edit
const editDomainConfiguration = (domainConfiguration) => {
    router.get(route(
        'settings.domain-configuration.edit',
        {
            domain_configuration: domainConfiguration.id
        }
    ))
}

// Delete
const deleteConfirmationModalVisible = ref(false)
const deleteDomainConfiguration = ref(() => {})
const showDeleteConfirmationModal = (domainConfiguration) => {
    deleteDomainConfiguration.value = () => router.delete(route(
        'settings.domain-configuration.destroy',
        {
            domain_configuration: domainConfiguration.id
        }
    ))
    deleteConfirmationModalVisible.value = true
}
const hideDeleteConfirmationModal = () => {
    deleteConfirmationModalVisible.value = false
}

function getSortDirection () {
    if (props.filters.sort_direction === null) {
        return null
    }

    return props.filters.sort_direction === 'asc' ? 1 : 0
}
</script>
